import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { newsApi } from '../lib/api';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Calendar,
  Tag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const NewsList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: news, isLoading, error } = useQuery({
    queryKey: ['news'],
    queryFn: () => newsApi.getAll().then((res: any) => res.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => newsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      toast.success('News deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete news');
    },
  });

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this news item?')) {
      deleteMutation.mutate(id);
    }
  };

  const filteredNews = news?.filter((item: any) =>
    item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.title_ar?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error loading news</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('news')}</h1>
          <p className="mt-2 text-gray-600">Manage your news articles</p>
        </div>
        <Link to="/news/create">
          <Button>
            <Plus className="h-5 w-5 mr-2" />
            {t('create')}
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search news..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* News List */}
      <Card>
        {filteredNews.length === 0 ? (
          <CardContent className="text-center py-12">
            <div className="text-gray-500 text-lg">{t('noData')}</div>
          </CardContent>
        ) : (
          <div className={`overflow-x-auto ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
            <Table className={i18n.language === 'ar' ? '[&_th]:text-right [&_td]:text-right' : '[&_th]:text-left [&_td]:text-left'}>
              <TableHeader>
                <TableRow>
                  <TableHead>{i18n.language === 'ar' ? 'العنوان' : 'Title'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'النوع' : 'Type'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'تاريخ النشر' : 'Published'}</TableHead>
                  <TableHead>{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNews.map((item: any) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {i18n.language === 'ar' ? (item.title_ar || item.title) : (item.title || item.title_ar)}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {i18n.language === 'ar' ? (item.excerpt_ar || item.excerpt) : (item.excerpt || item.excerpt_ar)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.status === 'published' ? 'default' : 'secondary'}>
                        {item.status || 'draft'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Tag className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm">
                          {item.type || 'paragraph'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm">
                          {item.published_at 
                            ? new Date(item.published_at).toLocaleDateString()
                            : 'Not published'
                          }
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Link to={`/news/edit/${item.id}`}>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default NewsList;
